import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { unsubscribe } from '../lib/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processUnsubscribe = async () => {
      // Prevent double execution (React StrictMode runs effects twice)
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;
      
      if (!token) {
        setStatus('error');
        setMessage('No unsubscribe token provided.');
        return;
      }

      try {
        const result = await unsubscribe(token);
        setStatus('success');
        setMessage('You have been successfully unsubscribed from Digital Law Digest. You will not receive any further emails from us. Your data has been permanently deleted.');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'This unsubscribe link is invalid or has already been used.');
      }
    };

    processUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6] noise-bg p-8">
      <div className="max-w-lg w-full text-center relative z-10 fade-in" data-testid="unsubscribe-page">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 bg-[#1A2E4A] flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
            <h1 className="text-3xl text-[#1A2E4A] mb-4">Processing...</h1>
            <p className="text-lg text-[#525C6A]">Please wait while we process your request.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#2E5A44] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl text-[#1A2E4A] mb-4" data-testid="unsubscribe-success-heading">
              Unsubscribed
            </h1>
            <p className="text-lg text-[#525C6A] leading-relaxed mb-8" data-testid="unsubscribe-success-message">
              {message}
            </p>
            <Link 
              to="/"
              className="inline-block bg-[#1A2E4A] hover:bg-[#2B4468] text-white px-8 py-3 font-semibold transition-colors duration-300"
              data-testid="unsubscribe-back-link"
            >
              Back to Digital Law Digest
            </Link>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-[#9B2C2C] flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl text-[#1A2E4A] mb-4" data-testid="unsubscribe-error-heading">
              Unsubscribe Failed
            </h1>
            <p className="text-lg text-[#525C6A] leading-relaxed mb-8" data-testid="unsubscribe-error-message">
              {message}
            </p>
            <Link 
              to="/"
              className="inline-block bg-[#1A2E4A] hover:bg-[#2B4468] text-white px-8 py-3 font-semibold transition-colors duration-300"
              data-testid="unsubscribe-back-link"
            >
              Back to Digital Law Digest
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
